import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAdminAuth } from "../../context/AdminAuthContext";
import {
  LayoutDashboard,
  CalendarCheck2,
  BadgeCheck,
  ClipboardList,
  Dumbbell,
  Building2,
  Newspaper,
  Image,
  Megaphone,
  Star,
  PartyPopper,
  CalendarDays,
  LayoutGrid,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Lock,
  LockOpen,
  X,
} from "lucide-react";

const operationalItems = [
  { label: "Dashboard",           path: "/admin-dashboard",                    icon: LayoutDashboard },
  { label: "Approve Reservation", path: "/admin-dashboard/reservationadmin",   icon: CalendarCheck2 },
  { label: "Approved Bookings",   path: "/admin-dashboard/approvedbookings",   icon: BadgeCheck },
  { label: "Manage Bookings",     path: "/admin-dashboard/managebookings",     icon: ClipboardList },
];

// Freely accessible - no premium lock.
const freeConfigItems = [
  { label: "Sports Setup",    path: "/admin-dashboard/sportssetup", icon: Dumbbell },
  { label: "Public Holidays", path: "/admin-dashboard/holidays",    icon: CalendarDays },
];

// Gated behind the premium admin lock (see AdminAuthContext / LockedSection).
const premiumConfigItems = [
  { label: "Facility Setup",    path: "/admin-dashboard/facilitysetup",    icon: Building2 },
  { label: "Home Facilities",   path: "/admin-dashboard/home-facilities",  icon: LayoutGrid },
  { label: "Blog",              path: "/admin-dashboard/blog",             icon: Newspaper },
  { label: "Gallery",           path: "/admin-dashboard/gallery",          icon: Image },
  { label: "Promotions",        path: "/admin-dashboard/promotions",       icon: Megaphone },
  { label: "Reviews",           path: "/admin-dashboard/reviews",          icon: Star },
  { label: "Events",            path: "/admin-dashboard/events",           icon: PartyPopper },
];

const navLinkClass = (collapsed) =>
  ({ isActive }) =>
    `group relative flex items-center gap-3 px-4 py-3 text-xs uppercase tracking-widest font-bold rounded-lg
    transition-all duration-200 ease-in-out
    ${collapsed ? "md:justify-center md:px-0" : ""}
    ${
      isActive
        ? "bg-red-700 text-white shadow-md shadow-red-900/30"
        : "text-white/50 hover:text-white hover:bg-white/[0.06] hover:translate-x-0.5"
    }`;

const NavItem = ({ item, collapsed, onCloseMobile, end, locked, unlocked }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={end}
      onClick={onCloseMobile}
      title={collapsed ? item.label : undefined}
      className={navLinkClass(collapsed)}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-4/5 w-1 rounded-full bg-white/80 ${collapsed ? "md:hidden" : ""}`} />
          )}
          <Icon size={16} className="shrink-0" />
          <span className={collapsed ? "md:hidden" : "flex-1 whitespace-nowrap text-left"}>{item.label}</span>
          {locked !== undefined && (
            <span
              title={unlocked ? "Unlocked for this session" : "Premium - locked"}
              className={`shrink-0 ${collapsed ? "md:hidden" : ""} ${unlocked ? "text-emerald-500/70" : "text-white/25"}`}
            >
              {unlocked ? <LockOpen size={13} /> : <Lock size={13} />}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};

const Sidebar = ({ collapsed, onToggleCollapsed, mobileOpen, onCloseMobile }) => {
  const { logout } = useAuth();
  const { premiumUnlocked } = useAdminAuth();
  const navigate   = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
    onCloseMobile?.();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 md:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed md:sticky top-0 left-0 h-screen z-50 shrink-0 bg-black border-r border-white/10 flex flex-col
          shadow-2xl shadow-black/60 md:shadow-none
          transition-all duration-300 ease-in-out
          ${collapsed ? "md:w-20" : "md:w-64"}
          w-72 max-w-[80vw]
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        <div className={`px-6 py-8 flex items-start justify-between ${collapsed ? "md:px-0 md:justify-center" : ""}`}>
          <div className={`${collapsed ? "md:hidden" : ""} overflow-hidden`}>
            <p className="text-xs uppercase tracking-[0.35em] text-red-600 font-semibold mb-1 whitespace-nowrap">
              Control Panel
            </p>
            <h2 className="text-xl font-black text-white uppercase tracking-tight whitespace-nowrap">Admin</h2>
          </div>
          {collapsed && (
            <p className="hidden md:flex items-center justify-center w-9 h-9 rounded-lg bg-red-700/10 border border-red-700/30 text-red-600 font-black text-lg">
              A
            </p>
          )}
          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close menu"
            className="md:hidden text-white/50 hover:text-white hover:bg-white/5 p-1.5 rounded-lg transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>

        <div className="mx-6 mb-2 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        <nav className="sidebar-scroll flex-1 min-h-0 px-4 py-4 flex flex-col gap-1 overflow-y-auto overflow-x-hidden">
          {!collapsed && (
            <p className="px-4 pt-1 pb-2.5 text-[9px] uppercase tracking-[0.3em] text-white/20 font-semibold whitespace-nowrap">
              Reservations
            </p>
          )}
          {operationalItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              collapsed={collapsed}
              onCloseMobile={onCloseMobile}
              end={item.path === "/admin-dashboard"}
            />
          ))}

          <div className="my-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {!collapsed && (
            <p className="px-4 pt-1 pb-2.5 text-[9px] uppercase tracking-[0.3em] text-white/20 font-semibold whitespace-nowrap">
              Configuration
            </p>
          )}
          {freeConfigItems.map((item) => (
            <NavItem key={item.path} item={item} collapsed={collapsed} onCloseMobile={onCloseMobile} />
          ))}

          <div className="my-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {!collapsed && (
            <p className="px-4 pt-1 pb-2.5 text-[9px] uppercase tracking-[0.3em] text-white/20 font-semibold whitespace-nowrap flex items-center gap-1.5">
              Premiums Config
              {!premiumUnlocked && <Lock size={9} className="text-white/20" />}
            </p>
          )}
          {premiumConfigItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              collapsed={collapsed}
              onCloseMobile={onCloseMobile}
              locked
              unlocked={premiumUnlocked}
            />
          ))}
        </nav>

        <div className="px-4 py-6 mt-auto border-t border-white/10 shrink-0">
          <button
            onClick={handleLogout}
            title={collapsed ? "Log Out" : undefined}
            className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg border border-white/15 hover:border-red-600 hover:bg-red-950/20 hover:text-red-500 text-white/50 font-bold uppercase tracking-widest text-xs transition-all duration-200 ${
              collapsed ? "md:justify-center md:px-0" : "justify-center"
            }`}
          >
            <LogOut size={14} className="shrink-0" />
            <span className={collapsed ? "md:hidden" : ""}>Log Out</span>
          </button>
        </div>

        {/* Desktop collapse toggle - sits on the right edge of the sidebar */}
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="hidden md:flex items-center justify-center absolute top-9 -right-3 w-6 h-6 rounded-full bg-black border border-white/15 text-white/60 shadow-md shadow-black/50 hover:text-white hover:border-red-600 hover:bg-red-950/40 transition-all duration-200"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>
    </>
  );
};

export default Sidebar;
